// The BigQuery proxy starts answering "504 stream timeout" when a page fires every
// indicator query at once - which is exactly what a country change does, since each visible
// card refetches from scratch. Two mitigations, both borrowed from the bulk download, which
// hit the same wall: cap how many requests are in flight, and retry the gateway-level
// failures, which are transient overload rather than a broken query and succeed on a retry.
// Anything else (e.g. a genuine 400) would fail again identically, so it is not retried.
const MAX_CONCURRENT = 4;
const MAX_ATTEMPTS = 3;
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const BACKOFF_MS = 600;

let inFlight = 0;
const waiting = [];

// Recursive rather than a loop: the trailing call fills the remaining free slots without
// closing over a loop variable.
const pump = () => {
    if (inFlight >= MAX_CONCURRENT || waiting.length === 0) return;
    const job = waiting.shift();
    inFlight += 1;
    job().finally(() => {
        inFlight -= 1;
        pump();
    });
    pump();
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const attempt = async (url, options) => {
    for (let n = 1; n <= MAX_ATTEMPTS; n += 1) {
        try {
            const response = await fetch(url, options);
            // The last response is handed back even when it failed, so callers keep reading
            // the proxy's error body and reporting what actually went wrong.
            if (response.ok || !RETRYABLE_STATUSES.has(response.status) || n === MAX_ATTEMPTS) {
                return response;
            }
        } catch (error) {
            if (error?.name === "AbortError" || n === MAX_ATTEMPTS) throw error;
        }
        // Jittered backoff: without it the whole page retries in lockstep and recreates the
        // burst that caused the timeouts.
        await sleep(BACKOFF_MS * 2 ** (n - 1) + Math.random() * 250);
    }
    throw new Error("Unreachable");
};

export const queuedFetch = (url, options) =>
    new Promise((resolve, reject) => {
        waiting.push(() => attempt(url, options).then(resolve, reject));
        pump();
    });
